/**
 * Push notifications on this device (Peter, 2026-11-04): a bell in the header shows the state, setup is per device,
 * and the steps depend on the device (iPhone Safari has to add the app to the Home Screen first).
 */
import { api } from "./api";
import { S, on, render } from "./state";
import { $, closeSheet, edHead, esc, openSheet, toast } from "./ui";

export type PushState = "unknown" | "unsupported" | "needs-install" | "off" | "on" | "blocked" | "failing";
export const P: { state: PushState; endpoint: string | null } = { state: "unknown", endpoint: null };

const ua = () => navigator.userAgent;
export const isIOS = () => /iPad|iPhone|iPod/.test(ua()) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/i.test(ua());
export const isStandalone = () =>
  matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function deviceName() {
  const app = isStandalone() ? "App" : "Browser";
  if (isIOS()) return `iPhone/iPad ${app}`;
  if (isAndroid()) return `Android ${app}`;
  const b = /Edg\//.test(ua()) ? "Edge" : /Firefox\//.test(ua()) ? "Firefox" : /Chrome\//.test(ua()) ? "Chrome" : /Safari\//.test(ua()) ? "Safari" : "Browser";
  const os = /Mac OS X/.test(ua()) ? "Mac" : /Windows/.test(ua()) ? "Windows" : /Linux/.test(ua()) ? "Linux" : "Computer";
  return `${b} on ${os}`;
}

function keyBytes(s: string) {
  const b = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
}

async function reg() {
  return navigator.serviceWorker.ready;
}

/** Works out this device's state. Safe to call often. */
export async function detectPush(): Promise<PushState> {
  let st: PushState;
  P.endpoint = null;
  if (!supported()) st = isIOS() && !isStandalone() ? "needs-install" : "unsupported";
  else if (Notification.permission === "denied") st = "blocked";
  else {
    try {
      const sub = await (await reg()).pushManager.getSubscription();
      if (!sub) st = "off";
      else {
        P.endpoint = sub.endpoint;
        const r = await api<{ on: boolean; failing: boolean }>("/api/push/status", { body: { endpoint: sub.endpoint } });
        st = r.on ? (r.failing ? "failing" : "on") : "off";
      }
    } catch {
      st = "off";
    }
  }
  const changed = st !== P.state;
  P.state = st;
  if (changed && S.me) render();
  return st;
}

export async function turnOn() {
  if (!supported()) return openSetup();
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    await detectPush();
    if (P.state === "blocked") openSetup();
    else toast("Notifications weren't turned on.");
    return;
  }
  try {
    const { publicKey } = await api<{ publicKey: string }>("/api/push/key");
    const r = await reg();
    let sub = await r.pushManager.getSubscription();
    if (!sub) sub = await r.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
    await api("/api/push/subscribe", { body: { ...sub.toJSON(), device: deviceName() } });
    closeSheet();
    toast("Notifications are on for this device");
  } catch (e) {
    toast((e as Error).message || "Couldn't turn on notifications.");
  }
  await detectPush();
  render();
}

export async function turnOff() {
  try {
    const sub = await (await reg()).pushManager.getSubscription();
    if (sub) {
      await api("/api/push/unsubscribe", { body: { endpoint: sub.endpoint } }).catch(() => {});
      await sub.unsubscribe();
    }
    toast("Notifications are off on this device. Tap the bell to turn them back on.");
  } catch (e) {
    toast((e as Error).message);
  }
  await detectPush();
  render();
}

const BELL =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';
const BELL_OFF =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8.7 3A6 6 0 0 1 18 8c0 2.8.6 4.8 1.3 6.2"/><path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/><path d="m2 2 20 20"/></svg>';

/** The header bell: plain when on, crossed out when off, crossed out in a red circle when blocked or failing. No text. */
export function bellHtml() {
  if (P.state === "unknown") return "";
  if (P.state === "on") return `<button class="bell" data-act="bell" aria-label="Notifications are on for this device. Tap to turn them off." title="Notifications on">${BELL}</button>`;
  const bad = P.state === "blocked" || P.state === "failing";
  return `<button class="bell ${bad ? "bad" : "off"}" data-act="bell" aria-label="${bad ? "Notifications need attention on this device" : "Turn on notifications on this device"}" title="${bad ? "Notifications need attention" : "Notifications off"}">${BELL_OFF}</button>`;
}

function steps(list: string[]) {
  return `<ol class="steps">${list.map((s) => `<li>${s}</li>`).join("")}</ol>`;
}

function settingsSteps() {
  if (isIOS())
    return steps([
      "Open the <b>Settings</b> app.",
      "Tap <b>Notifications</b>, then <b>Ambassadors</b>.",
      "Turn on <b>Allow Notifications</b>.",
      "Come back here and tap the bell again.",
    ]);
  if (isAndroid())
    return steps([
      "Tap the lock or settings icon next to the web address (or open the app's settings).",
      "Tap <b>Permissions</b> (or <b>Site settings</b>), then <b>Notifications</b>.",
      "Choose <b>Allow</b>.",
      "Come back here and tap the bell again.",
    ]);
  return steps([
    "Click the lock or settings icon at the left of the address bar.",
    "Set <b>Notifications</b> to <b>Allow</b>.",
    "Reload this page and click the bell again.",
  ]);
}

/** The setup box for this device, or the "needs attention" box when blocked or failing. */
export function openSetup(fromSignIn = false) {
  const st = P.state;
  const bad = st === "blocked" || st === "failing";
  const title = bad ? "Notifications Need Attention on This Device" : "Turn On Notifications on This Device";
  let body = "";
  let buttons = "";
  if (st === "needs-install") {
    body = `<p>On iPhone and iPad, notifications only work from the app on your Home Screen.</p>${steps([
      "Tap the <b>Share</b> button at the bottom of Safari (the square with an arrow).",
      "Scroll down and tap <b>Add to Home Screen</b>, then <b>Add</b>.",
      "Open <b>Ambassadors</b> from your Home Screen.",
      "Sign in there with a new code (the app keeps its own sign-in).",
      "Tap the bell at the top to turn on notifications.",
    ])}`;
    buttons = `<button class="btn ghost block" data-act="close-sheet">Continue in Browser with No Notifications</button>`;
  } else if (st === "unsupported") {
    body = `<p>This browser can't show notifications. Chrome, Edge, Firefox or Safari will work.</p>`;
    buttons = `<button class="btn ghost block" data-act="close-sheet">Close</button>`;
  } else if (st === "blocked") {
    body = `<p>Notifications are blocked for this site on this device, so you'll miss new events and last-minute openings.</p>${settingsSteps()}`;
    buttons = `<button class="btn ghost block" data-act="close-sheet">Close</button>`;
  } else if (st === "failing") {
    body = `<p>Notifications to this device stopped going through. Turning them off and back on usually fixes it.</p>`;
    buttons = `<button class="btn block" data-act="push-reset">Turn Off and Back On</button><button class="btn ghost block" data-act="close-sheet">Not Now</button>`;
  } else {
    body = `<p>Get a notification on this ${isIOS() || isAndroid() ? "phone" : "device"} when new events are posted and when a spot opens up at the last minute.</p><p class="muted small">You can turn them off any time by tapping the bell at the top.</p>`;
    buttons = `<button class="btn block" data-act="push-on">Turn On Notifications</button><button class="btn ghost block" data-act="close-sheet">${fromSignIn ? "Not Now" : "Cancel"}</button>`;
  }
  openSheet(`${edHead(esc(title))}<div class="stack" style="margin-top:14px">${bad ? `<div class="note bad">${esc(title)}</div>` : ""}${body}<div class="stack" style="gap:8px">${buttons}</div></div>`);
}

/** Right after signing in: offer setup once per device while notifications are off there. */
export async function offerAfterSignIn() {
  const st = await detectPush();
  if (st !== "off" && st !== "needs-install") return;
  try {
    if (localStorage.getItem("pushOffered")) return;
    localStorage.setItem("pushOffered", "1");
  } catch {
    /* private mode: offer anyway */
  }
  openSetup(true);
}

/** Profile row: "Notifications on This Device". */
export function profilePushRow() {
  const label: Record<PushState, string> = {
    unknown: "Checking…",
    on: "On",
    off: "Off",
    "needs-install": "Off (add the app to your Home Screen first)",
    unsupported: "Not available in this browser",
    blocked: "Blocked",
    failing: "Needs attention",
  };
  const st = P.state;
  let btns = "";
  if (st === "on") btns = `<button class="btn ghost sm" data-act="push-test">Send Me a Test</button><button class="btn ghost sm" data-act="push-off">Turn Off</button>`;
  else if (st === "off" && "Notification" in window && Notification.permission === "granted")
    btns = `<button class="btn sm" data-act="push-on">Turn Back On</button>`;
  else if (st !== "unknown") btns = `<button class="btn sm" data-act="push-setup">${st === "blocked" || st === "failing" ? "Fix" : "Turn On"}</button>`;
  return `<div class="row between wrap" style="gap:8px"><div><b>Notifications on This Device</b><div class="small ${st === "blocked" || st === "failing" ? "warn-text" : "muted"}">${label[st]}</div></div><div class="row wrap" style="gap:6px">${btns}</div></div>`;
}

on({
  bell: async () => {
    if (P.state === "on") return turnOff();
    // Turning back on needs no steps when this device already allows notifications.
    if (P.state === "off" && "Notification" in window && Notification.permission === "granted") return turnOn();
    openSetup();
  },
  "push-on": () => turnOn(),
  "push-off": () => turnOff(),
  "push-setup": () => openSetup(),
  "push-reset": async () => {
    await turnOff();
    await turnOn();
  },
  "push-test": async () => {
    try {
      await api("/api/push/test", { body: { endpoint: P.endpoint } });
      toast("Test sent. It should arrive in a few seconds.");
    } catch (e) {
      toast((e as Error).message);
      await detectPush();
    }
  },
});

// Re-check when the person comes back to the app (they may have changed settings).
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && S.me) void detectPush();
});
void $;
